import React from 'react';
import { Button } from '@/components/shared/button';
import { Save } from 'lucide-react';
import { Weights, OpportunityScore } from '@/types/opportunity';
import { useSaveAssessment } from '@/hooks/useSaveAssessment';
import { toast } from 'sonner';
import { useAssessment } from '@/contexts/AssessmentContext';

interface SaveAssessmentButtonProps {
  weights: Weights;
  scoredOpportunities: OpportunityScore[];
}

const SaveAssessmentButton = ({ weights, scoredOpportunities }: SaveAssessmentButtonProps) => {
  const { scoringParams } = useAssessment();
  
  // Create extended weights object that includes scoringParams
  const extendedWeights = {
    ...weights,
    _scoringParams: scoringParams
  };
  
  const { handleSave, isSaving } = useSaveAssessment(extendedWeights, scoredOpportunities);

  const onSave = async () => {
    console.log('Starting save with data:', { weights: extendedWeights, scoredOpportunities });
    
    toast.loading('Saving assessment...', { id: 'save-assessment' });

    try {
      await handleSave();
      console.log('Save completed successfully');
      
      // Dispatch custom event to notify components that the assessment was saved
      window.dispatchEvent(new Event('assessment:saved'));
      
      toast.success('Assessment saved successfully!', { id: 'save-assessment' });
    } catch (error) {
      console.error('Error during save:', error);
      
      toast.error('Failed to save assessment. Please try again.', { id: 'save-assessment' });
    }
  };

  return (
    <Button
      variant="outline"
      onClick={onSave}
      className="gap-2"
      disabled={isSaving}
    >
      <Save className="w-4 h-4" />
      {isSaving ? 'Saving...' : 'Save'}
    </Button>
  );
};

export default SaveAssessmentButton;